/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Yup from "yup";
import useAuthHttp from "../../hooks/useAuthHttp";
import { useMutation } from "@tanstack/react-query";
import moment from "moment";
import { AssetMainType, IAssetInfo } from "../../models/asset";
import { useFormik } from "formik";
import { AssetSummary } from "../../commons/assetSummary";
import { Toast } from "primereact/toast";
import { useEffect, useRef } from "react";
import AssetSearchSelector from "../AssetSearchSelector";
import View from "../Container";
import Text from "../Text";
import Label from "../Label";
import FormField from "../FormField";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import Button from "../Button";
import ButtonGroup from "../ButtonGroup";
import { useExchangeRateByType } from "../../hooks/useExchangeRateByType";
import Show from "../Show";

interface IWithdrawForm {
    selectedAsset?: AssetSummary;
    date?: Date;
    amount?: number;
    withdrawType?: number;
    quantity?: number;
}

interface IWithdrawHandleFormProps {
    onSuccess: () => void;
}

const WithdrawHandleForm = ({ onSuccess }: IWithdrawHandleFormProps) => {
    const http = useAuthHttp();
    const toast = useRef<Toast | null>(null);
    const chargeMutation = useMutation({
        mutationFn: async (form: IWithdrawForm) => {
            const body = {
                event_date: moment(form.date).format("YYYY-MM-DD HH:mm:ss"),
                event_value: form.amount,
                event_quantity: form.quantity,
                event_type: form.withdrawType == 1 ? "REDEEM" : "WITHDRAW"
            };

            return await http.doPatch<any, IAssetInfo>(`asset/${form.selectedAsset?.id}/withdraw`, body);
        }
    });

    const formik = useFormik<IWithdrawForm>({
        initialValues: {
            selectedAsset: undefined,
            date: undefined,
            withdrawType: undefined,
            quantity: undefined,
            amount: undefined
        },
        onSubmit: async (values) => {
            await send(values);
        },
        validationSchema: () => Yup
            .object()
            .shape({
                selectedAsset: Yup.object().required("Campo obrigatório"),
                date: Yup.date().required("Campo obrigatório"),
                withdrawType: Yup.number().required("Campo obrigatório"),
                quantity: Yup.number()
                    .when(
                        "selectedAsset",
                        ([selectedAsset], schema) => {
                            if (
                                selectedAsset?.type === AssetMainType.Cripto
                                || selectedAsset?.type === AssetMainType.Currency
                                || selectedAsset?.type === AssetMainType.InvestimentFunds
                                || selectedAsset?.type === AssetMainType.Stock
                            ) {
                                return schema.required("Campo obrigatório").min(0.0001, "Valor deve ser maior que 0");
                            }


                            return schema;
                        }
                    )
                    .nullable(),
                amount: Yup.number()
                    .when(
                        "selectedAsset",
                        ([selectedAsset], schema) => {
                            if (!selectedAsset) {
                                return schema.required("Selecione um ativo");
                            }

                            return schema;
                        }
                    )
                    .required("Campo obrigatório")
            })
    });

    const { exchangeRateQuery } = useExchangeRateByType(
        formik.values.selectedAsset?.type?.toLowerCase() as AssetMainType, 
        formik.values.date, 
        formik.values.selectedAsset?.name.split("-")[0],
        (cotation: number) => {
            formik.setFieldValue("cotation", cotation);
        }
    );

    useEffect(() => {
        const body = document.getElementsByTagName("body")[0];
        body.style.overflow = "hidden";

        return () => {
            body.style.overflow = "auto";
        }
    }, []);

    const {
        isPre,
        indexCorrelationName,
        indexCorrelation,
        issuingBankName,
        negotiatingBankName,
        dueDate,
        purchaseDate,
        brokerageValue,
        othersValue,
        monthlyCosts,
        quantity,
        yearTax,
        cotation,
        recurringValue,
        pbType
    } = formik.values.selectedAsset ?? {};

    const toCurrency = (value: number) => {
        return Intl.NumberFormat(
            "pt-BR",
            {
                style: "currency",
                currency: "BRL"
            }
        )
            .format(value);
    }

    const infos: { label: string; value: string; }[] = [];
    if (pbType) {
        infos.push({
            label: "Ativo",
            value: `${pbType} ${isPre ? "pré-fixado" : "pós-fixado"}`
        });
    }

    if (indexCorrelation) {
        infos.push({
            label: "Taxa",
            value: `${indexCorrelationName} + ${indexCorrelation}%`
        });
    }

    if (dueDate) {
        infos.push({
            label: "Vencimento",
            value: moment(dueDate).format("DD/MM/YYYY")
        });
    }

    if (purchaseDate) {
        infos.push({
            label: "Compra",
            value: moment(purchaseDate).format("DD/MM/YYYY")
        });
    }

    if (issuingBankName) {
        infos.push({
            label: "Emissor",
            value: issuingBankName
        });
    }

    if (negotiatingBankName) {
        infos.push({
            label: "Corretora",
            value: negotiatingBankName
        });
    }

    if (brokerageValue || othersValue) {
        infos.push({
            label: "Custos Operacionais",
            value: toCurrency((brokerageValue ?? 0) + (othersValue ?? 0))
        });
    }

    if (monthlyCosts) {
        infos.push({
            label: "Custos Mensais",
            value: toCurrency((monthlyCosts ?? 0))
        });
    }

    if (quantity) {
        infos.push({
            label: "Quantidade",
            value: quantity.toString()
        });
    }

    if (yearTax) {
        infos.push({
            label: "Taxa anual",
            value: `${yearTax}%`
        });
    }

    if (recurringValue) {
        infos.push({
            label: "Valor recorrente",
            value: toCurrency((recurringValue ?? 0))
        });
    }

    if (cotation) {
        infos.push({
            label: "Cotação",
            value: toCurrency((cotation ?? 0))
        });
    }

    const send = async (form: IWithdrawForm) => {
        try {
            const res = await chargeMutation.mutateAsync(form);

            if ((res?.status ?? 0) > 299 || (res?.status ?? 0) <= 0) {
                throw new Error("Falha ao cadastrar investimento");
            }

            onSuccess();
        } catch (err) {
            toast.current?.show({
                severity: "error",
                summary: "Falha ao cadastrar investimento"
            });
        }
    }

    const withdrawType = [
        {
            id: 1,
            name: "Total"
        },
        {
            id: 2,
            name: "Parcial"
        }
    ];

    return (
        <>
            <Toast ref={toast} />

            <AssetSearchSelector
                label="Informe o ativo"
                hasError={!!formik.errors.selectedAsset && formik.touched.selectedAsset}
                error={formik.errors.selectedAsset}
                selected={formik.values.selectedAsset}
                onSelectBank={(asset: AssetSummary | null) => {
                    if (asset != formik.values.selectedAsset) {
                        formik.setFieldValue("amount", asset?.totalValue ?? 0);
                        formik.setFieldValue("selectedAsset", asset);
                    }
                }}
            />

            <FormField
                id="date"
                label="Data do resgate"
                hasError={!!formik.errors.date && formik.touched.date}
                error={formik.errors.date}
            >
                <Calendar
                    invalid={!!formik.errors.date && formik.touched.date}
                    value={formik.values.date}
                    dateFormat="dd/mm/yy"
                    readOnlyInput
                    showIcon
                    maxDate={new Date()}
                    disabledDays={[0, 6]}
                    icon={exchangeRateQuery.isLoading ? "pi pi-spin pi-spinner" : "pi pi-calendar"}
                    onChange={(event) => {
                        formik.setFieldValue("date", event.value)
                    }}
                    locale="pt"
                />
            </FormField>

            <View flexColumn>
                <Text>
                    Valor do resgate
                </Text>

                <View
                    className={`flex flex-row flex-wrap items-center mt-3 gap-2 rounded`}
                >
                    <ButtonGroup
                        invalid={!!formik.errors.withdrawType && formik.touched.withdrawType}
                        group={withdrawType.map(g => (
                            {
                                name: g.name,
                                disabled: !formik.values.selectedAsset,
                                selected: formik.values.withdrawType == g.id,
                                onClick: () => {
                                    if (g.id == 1) {
                                        formik.setFieldValue("quantity", formik.values.selectedAsset?.quantity ?? 0);
                                        formik.setFieldValue("amount", formik.values.selectedAsset?.totalValue ?? 0);
                                    }

                                    formik.setFieldValue("withdrawType", g.id);
                                }
                            })) ?? []
                        }
                    />
                </View>

                {
                    !!formik.errors.withdrawType && formik.touched.withdrawType && (
                        <Text className="text-red-500 mt-2" size="xs">{formik.errors.withdrawType}</Text>
                    )
                }
            </View>

            <Show when={
                formik.values.selectedAsset?.type === AssetMainType.Cripto
                || formik.values.selectedAsset?.type === AssetMainType.Currency
                || formik.values.selectedAsset?.type === AssetMainType.InvestimentFunds
                || formik.values.selectedAsset?.type === AssetMainType.Stock
            }>
                <FormField
                    id="quantity"
                    hasError={!!formik.errors.quantity && formik.touched.quantity}
                    error={formik.errors.quantity}
                    label={`Quantidade (Máx: ${formik.values.selectedAsset?.quantity ?? 0})`}
                >
                    <InputNumber
                        invalid={!!formik.errors.quantity && formik.touched.quantity}
                        value={formik.values.quantity}
                        disabled={formik.values.withdrawType == 1}
                        max={formik.values.selectedAsset?.quantity ?? 0}
                        min={0.0001}
                        minFractionDigits={2}
                        maxFractionDigits={10}
                        onChange={(e) => {
                            const quantity = e.value ?? 0;

                            if (quantity != formik.values.quantity) {
                                formik.setFieldValue("quantity", quantity);
                            }

                            const cotation = ((formik.values.selectedAsset?.totalValue ?? 0) / (formik.values.selectedAsset?.quantity ?? 0));
                            const amount = cotation * (quantity ?? 0);

                            if (amount != formik.values.amount) {
                                formik.setFieldValue("amount", amount);
                            }
                        }}
                        locale="pt-BR"
                    />
                </FormField>
            </Show>

            <FormField
                id="amount"
                hasError={!!formik.errors.amount && formik.touched.amount}
                error={formik.errors.amount}
                label={`Valor total do resgate`}
            >
                <InputNumber
                    invalid={!!formik.errors.amount && formik.touched.amount}
                    disabled={!!formik.errors.selectedAsset || !formik.values.withdrawType}
                    min={0.0001}
                    onChange={(e) => {
                        const amount = e.value ?? 0;

                        if (amount != formik.values.amount) {
                            formik.setFieldValue("amount", amount);
                            const quantity = (amount * (formik.values.selectedAsset?.quantity ?? 0)) / (formik.values.selectedAsset?.totalValue ?? 0);
                            formik.setFieldValue("quantity", quantity);
                        }
                    }}
                    value={formik.values.amount}
                    mode="currency"
                    currency="BRL"
                    locale="pt-BR"
                />
            </FormField>

            {
                !!infos.length && <View flexColumn gap={2}>
                    <Label className="border-bottom-1 border-gray-300 pb-2">
                        Dados do Investimento
                    </Label>

                    <View className="flex flex-column items-between gap-3">
                        {
                            infos.map(info => {
                                return (
                                    <View key={info.label} className="flex flex-column flex-row justify-between">
                                        <Label>{info.label}</Label>

                                        <Text className="text-xs max-w-xs">{info.value}</Text>
                                    </View>
                                )
                            })
                        }
                    </View>
                </View>
            }

            <View fullWidth>
                <Button
                    onClick={() => {
                        formik.handleSubmit();
                        return Promise.resolve();
                    }}
                    className="w-full justify-content-center"
                    loading={chargeMutation.isPending}
                    disabled={chargeMutation.isPending}
                >
                    Cadastrar
                </Button>
            </View>
        </>
    )
}

export default WithdrawHandleForm;
